<script setup lang="ts">
import type { Album, EventVault, Media } from "@/models"
import cover from "@brand-cover"
import { computed } from "vue"
import { GuestInvitationModal, GuestReminderModal, WelcomeVideoModal } from "@/components"
import { BaseModal, Button, Icon, ReadMore } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { GalleryHeroCoverModalKey, GuestInvitationModalKey, GuestReminderModalKey, WelcomeVideoModalKey } from "@/symbols"
import { convertDateTimezone } from "@/utils"

interface GalleryHeroImageProps {
  eventVault?: EventVault
  album?: Album
  welcomeVideo?: Media
  isLoading?: boolean
  isUploadsEnabled?: boolean
  zipDownloadUrl?: string
}

const props = withDefaults(defineProps<GalleryHeroImageProps>(), {
  isLoading: false,
  isUploadsEnabled: false,
  zipDownloadUrl: undefined,
})

const emit = defineEmits<{
  (e: "upload"): void
  (e: "scrollToGallery"): void
}>()

const { d, t } = useTranslation()

const { toggle: toggleWelcomeVideoModal } = useModal(WelcomeVideoModalKey)
const { toggle: toggleCoverModal } = useModal(GalleryHeroCoverModalKey)
const { toggle: toggleGuestInvitationModal } = useModal(GuestInvitationModalKey)
const { toggle: toggleGuestReminderModal } = useModal(GuestReminderModalKey)

const heroDetails = computed(() => {
  if (props.album) {
    return {
      title: props.album.title,
      description: props.album.description,
      coverImageUrl: props.album.coverImageUrl ?? cover,
      date: props.album.date ? d(props.album.date, "medium") : undefined,
      location: props.album.location,
    }
  }

  return {
    title: props.eventVault?.eventHeader ?? props.eventVault?.title,
    description: props.eventVault?.description,
    coverImageUrl: props.eventVault?.coverImageUrl ?? cover,
    date: props.eventVault?.timezone ? d(convertDateTimezone(props.eventVault.eventDate ?? new Date(), props.eventVault.timezone), "long") : d(props.eventVault?.eventDate ?? new Date(), "medium"),
    location: props.eventVault?.location,
    welcomeVideo: props.welcomeVideo,
  }
})
</script>

<template>
  <div class="relative flex min-h-[90vh] w-full shrink-0 flex-col justify-end overflow-hidden 2xl:min-h-[64vh]" data-test="gallery-event-hero">
    <template v-if="isLoading">
      <div class="absolute -inset-px z-0 bg-[radial-gradient(circle_at_top,rgb(255_255_255/0.12),rgb(255_255_255/0)_58%)]" />
      <div class="container relative z-10 mx-auto px-4 pb-28 pt-24 md:pb-36 md:pt-28" data-test="gallery-event-hero-loading">
        <div class="flex max-w-3xl flex-col md:max-w-4xl md:flex-row md:items-end md:gap-8">
          <div class="mb-6 shrink-0 md:mb-0">
            <div class="relative isolate size-40 overflow-hidden rounded-full border-2 border-white/30 md:size-48 lg:size-56">
              <div class="gpx-skeleton absolute inset-0 rounded-full" />
            </div>
          </div>
          <div class="min-w-0 flex-1 space-y-4 md:space-y-5">
            <div class="gpx-skeleton h-7 w-40 rounded-full md:w-52" />
            <div class="space-y-2.5">
              <div class="gpx-skeleton h-10 w-3/4 rounded-lg md:h-12" />
              <div class="gpx-skeleton h-5 w-60 rounded-md" />
            </div>
            <div class="space-y-2.5">
              <div class="gpx-skeleton h-4 w-full max-w-2xl rounded-md" />
              <div class="gpx-skeleton h-4 w-11/12 rounded-md" />
            </div>
            <div class="flex w-full flex-col items-stretch gap-3 pt-4 md:flex-row md:flex-wrap">
              <div class="gpx-skeleton h-11 w-full rounded-lg md:w-48" />
            </div>
            <div class="gpx-skeleton h-6 w-56 rounded-md" />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="absolute inset-0 z-0 bg-black" aria-hidden="true" />
      <div
        class="absolute z-0 bg-cover bg-center bg-no-repeat blur-sm max-md:-inset-6 max-md:scale-[1.2] md:-inset-px md:scale-105"
        :style="{ backgroundImage: `linear-gradient(to top, rgb(0 0 0 / 65%), rgb(0 0 0 / 25%)), url(${heroDetails.coverImageUrl})` }"
      />

      <div class="container relative z-10 mx-auto px-4 pb-28 pt-24 md:pb-36 md:pt-28">
        <div class="flex max-w-3xl flex-col md:max-w-6xl md:flex-row md:items-end md:gap-8">
          <div class="relative mb-6 shrink-0 md:mb-0">
            <button
              v-if="heroDetails.welcomeVideo"
              class="group relative inline-flex cursor-pointer items-center justify-center rounded-full bg-transparent p-0 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              data-test="gallery-event-hero-play-welcome-video"
              @click="toggleWelcomeVideoModal"
            >
              <span class="pointer-events-none absolute inset-[-4px] z-0 rounded-full border-2 border-white/45 animate-[gallery-hero-ring-pulse_10s_ease-in-out_infinite]" />
              <span class="pointer-events-none absolute inset-[-4px] z-0 rounded-full border-2 border-white/45 animate-[gallery-hero-ring-pulse_10s_ease-in-out_infinite] [animation-delay:5s]" />
              <span class="relative block size-40 overflow-hidden rounded-full border-2 border-white shadow-lg transition-transform duration-200 ease-in-out motion-reduce:transform-none motion-reduce:transition-none group-hover:scale-[1.03] group-focus-visible:scale-[1.03] md:size-48 lg:size-56">
                <img :src="heroDetails.coverImageUrl" class="size-full object-cover">
              </span>
              <span class="absolute flex size-full items-center justify-center rounded-full bg-transparent transition-[background] duration-200 ease-in-out motion-reduce:transition-none group-hover:bg-black/40 group-focus-visible:bg-black/40">
                <Icon icon="Play" size="lg" class="text-white transition-transform duration-200 ease-in-out motion-reduce:transform-none motion-reduce:transition-none group-hover:scale-110 group-focus-visible:scale-110 [filter:drop-shadow(0_0_1px_rgb(0_0_0_/_0.85))_drop-shadow(0_2px_10px_rgb(0_0_0_/_0.5))]" />
              </span>
            </button>
            <button
              v-else
              class="group relative inline-flex cursor-pointer items-center justify-center rounded-full bg-transparent p-0 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              data-test="gallery-event-hero-cover"
              @click="toggleCoverModal"
            >
              <span class="relative block size-40 overflow-hidden rounded-full border-2 border-white shadow-lg md:size-48 lg:size-56">
                <img :src="heroDetails.coverImageUrl" :alt="heroDetails.title ?? ''" class="size-full object-cover">
                <span class="pointer-events-none absolute inset-0 rounded-full bg-black/0 transition-[background-color] duration-200 ease-in-out motion-reduce:transition-none group-hover:bg-black/30 group-focus-visible:bg-black/30" />
              </span>
            </button>
          </div>

          <div class="min-w-0 flex-1 space-y-4 md:space-y-5" data-test="gallery-event-hero-details">
            <div v-if="heroDetails.date">
              <span class="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-white/95 backdrop-blur-sm">
                <Icon icon="Calendar" class="size-3 shrink-0" />
                {{ heroDetails.date }}
              </span>
            </div>

            <div class="space-y-2 md:space-y-2.5">
              <h1 class="text-2xl font-semibold tracking-tight text-white drop-shadow-sm sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
                {{ heroDetails.title }}
              </h1>
              <p v-if="album" class="flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 leading-tight text-white/70 text-xs md:text-sm">
                <template v-if="eventVault?.eventHeader || eventVault?.title">
                  <span class="min-w-0 font-medium text-white/80" :title="eventVault?.eventHeader ?? eventVault?.title">{{ eventVault?.eventHeader ?? eventVault?.title }}</span>
                </template>
              </p>
            </div>

            <ReadMore
              v-if="heroDetails.description"
              :content="heroDetails.description"
              :lines="4"
              class="max-w-2xl text-sm leading-snug text-white/85 sm:text-base sm:leading-relaxed md:text-lg md:leading-relaxed"
              data-test="gallery-event-hero-description"
            >
              <template #default="{ toggle, expanded, canExpand }">
                <button v-if="canExpand" class="mt-1.5 w-full cursor-pointer text-left text-xs font-medium text-white/95 underline decoration-white/45 decoration-2 underline-offset-4 transition-colors hover:text-white hover:decoration-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:mt-2 sm:text-sm sm:underline-offset-[5px]" @click="toggle">
                  {{ expanded ? t("gallery_hero_image.read_less") : t("gallery_hero_image.read_more") }}
                </button>
              </template>
            </ReadMore>

            <div class="flex w-full flex-col items-stretch gap-3 pt-4 md:flex-row md:flex-wrap">
              <Button v-if="isUploadsEnabled" variant="secondary" class="w-full md:w-auto" prefix-icon="ImagePlus" data-test="gallery-event-hero-upload-photos" @click="emit('upload')">
                {{ eventVault?.eventVaultSettings?.videoEnabled ? t("upload_photos_and_videos") : t("upload_photos") }}
              </Button>
              <Button v-if="eventVault?.eventVaultSettings?.invitationEnabled && !album" variant="secondary" class="w-full md:w-auto" prefix-icon="Mail" data-test="gallery-event-hero-view-invitation" @click="toggleGuestInvitationModal">
                {{ t("gallery_hero_image.view_invitation") }}
              </Button>
              <Button
                v-if="zipDownloadUrl"
                class="w-full md:w-auto"
                variant="secondary"
                prefix-icon="Download"
                :href="zipDownloadUrl"
                target="_blank"
                rel="noopener noreferrer"
                data-test="gallery-event-hero-download-zip"
              >
                {{ t("dashboard_page.menu.download_zip") }}
              </Button>
            </div>

            <button
              v-if="isUploadsEnabled"
              type="button"
              data-test="gallery-event-hero-reminder-link"
              class="flex w-fit max-w-full cursor-pointer items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-left text-xs font-medium text-white/90 underline decoration-white/45 decoration-2 underline-offset-4 transition-colors hover:text-white hover:decoration-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent sm:gap-2 sm:px-2 sm:py-2 sm:text-sm sm:underline-offset-[5px]"
              @click="toggleGuestReminderModal"
            >
              <Icon icon="CalendarClock" class="size-3.5 shrink-0 opacity-90 sm:size-4" />
              {{ t("gallery_hero_image.remind_me_to_upload_later") }}
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        data-test="gallery-event-hero-scroll-to-gallery"
        class="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 cursor-pointer flex-col items-center gap-1 rounded border-0 bg-transparent text-white/70 transition-colors hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent md:flex"
        @click="emit('scrollToGallery')"
      >
        <span class="mb-1 text-xs font-medium uppercase tracking-widest">
          {{ t("gallery_hero_image.explore") }}
        </span>
        <Icon icon="CircleChevronDown" class="size-6 animate-bounce" />
      </button>

      <button
        type="button"
        data-test="gallery-event-hero-scroll-to-gallery-mobile"
        class="absolute right-4 top-8 z-20 flex cursor-pointer flex-col items-center gap-1 rounded border-0 bg-transparent text-white/70 transition-colors hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent md:hidden"
        @click="emit('scrollToGallery')"
      >
        <span class="max-w-20 mb-1 text-center text-[10px] font-medium uppercase tracking-[0.12em] leading-tight">
          {{ t("gallery_hero_image.scroll_to_explore") }}
        </span>
        <Icon icon="Pointer" size="sm" class="animate-bounce rotate-180" />
      </button>

      <BaseModal v-if="!heroDetails.welcomeVideo" :name="GalleryHeroCoverModalKey" allow-overflow>
        <div class="flex w-full min-w-0 justify-center guest-layout">
          <div class="relative w-fit max-w-full">
            <img
              :src="heroDetails.coverImageUrl"
              :alt="heroDetails.title ?? ''"
              class="mx-auto block max-h-[80vh] w-auto max-w-full object-contain"
            >
            <Button
              type="button"
              shape="circle"
              variant="secondary"
              size="sm"
              class="absolute right-2 top-2 z-10"
              @click="toggleCoverModal"
            >
              <Icon icon="Close" />
            </Button>
          </div>
        </div>
      </BaseModal>

      <WelcomeVideoModal
        v-if="heroDetails.welcomeVideo"
        :media="heroDetails.welcomeVideo"
        :poster-url="heroDetails.coverImageUrl"
      />

      <GuestInvitationModal
        v-if="eventVault"
        :event-vault="eventVault"
      />

      <GuestReminderModal
        v-if="eventVault?.id"
        :event-vault-id="eventVault.id"
      />
    </template>
  </div>
</template>

<style scoped>
@keyframes gallery-hero-ring-pulse {
  0% {
    transform: scale(1);
    opacity: 0.35;
  }
  8% {
    transform: scale(1.04);
    opacity: 0.6;
  }
  12% {
    transform: scale(1.04);
    opacity: 0.6;
  }
  16% {
    transform: scale(1);
    opacity: 0.35;
  }
  16%,
  100% {
    transform: scale(1);
    opacity: 0.35;
  }
}
</style>
